import React, { useEffect, useMemo, useRef, useState } from 'react';

import { sendGtEvent } from 'apps/auth/analytics/analytics';
import { appendToUserObjectArray, sendBrazeEvent } from 'apps/auth/analytics/braze';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import RecipientList from 'apps/auth/components/common/recipientList/RecipientList';
import { SelectCountryScreenPureComponent } from 'apps/auth/components/common/search/SelectCountryScreen';
import ChooseWhoToSendPopup from 'apps/auth/components/history/send/popup/ChooseWhoToSendPopup';
import BasePage from 'apps/auth/components/utils/BasePage';
import { BR_SEND_COUNTRY_TO_SELECTED } from 'apps/auth/constants/braze/brazeEvents';
import { BRAZE_IAM_PLACEMENTS, BRAZE_IAM_SCREEN_EVENTS } from 'apps/auth/constants/braze/brazeIam';
import { BR_COUNTRY, BR_COUNTRY_CODE, BR_RECIPIENT_COUNTRIES, BR_SELECTED_DATE } from 'apps/auth/constants/braze/brazeParams';
import { GT_NEW_TRANSFER_START } from 'apps/auth/constants/GoogleAnalyticsEvents';
import {
    AUTH_BONUS_WITHDRAW,
    AUTH_GLOBAL,
    AUTH_GLOBAL_BONUS_WITHDRAW,
    AUTH_PROFILE_BONUS,
    AUTH_SEND,
    AUTH_SEND_LINK,
    AUTH_SEND_MAIN_LINK,
} from 'apps/auth/constants/UrlConstants';
import { toUpperCase } from 'apps/auth/helpers/utils';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import { useBrazeIamScreen } from 'apps/auth/hooks/useBrazeIamScreen';
import { useFlags } from 'apps/auth/hooks/useFlags';
import { useQuery } from 'apps/auth/hooks/useQuery';

const SendSelectCountryPage = ({ stateOptions: { isBonus }, ...props }) => {
    const { show_currencies_in_countries_list } = useFlags();
    const { countriesTo, userCountry, recipientList = [], globalSources, searchCountryName, chooseWhoToSendPopup } = useExtra();
    const {
        title,
        titleWithOutGlobalMethods,
        countriesHeader,
        searchPlaceholder,
        searchByCountryPlaceholder,
        searchNoResult,
        gotIt,
        notificationText,
        recipientHeader,
        recipientListSubtitle,
    } = usePageMessages();
    const query = useQuery();
    const showCurrency = show_currencies_in_countries_list;
    const hideRecipientList = useMemo(() => !!searchCountryName || query.get('hideRecipientList') === 'true', []);
    const [showRecipientList, toggleRecipientList] = useState(!hideRecipientList);
    const popupRef = useRef(null);

    useEffect(() => {
        if (__isBrowser__ && !isBonus) {
            sendGtEvent(GT_NEW_TRANSFER_START);
        }
    }, []);

    useBrazeIamScreen(BRAZE_IAM_SCREEN_EVENTS.SEND_MONEY, BRAZE_IAM_PLACEMENTS.SEND_MONEY, {}, { enabled: !isBonus });

    useEffect(() => {
        if (Array.isArray(recipientList) && recipientList.length > 0) {
            toggleRecipientList(!hideRecipientList);
        }
    }, [recipientList]);

    const sendGlobalUrIPrefix = isBonus ? AUTH_GLOBAL_BONUS_WITHDRAW : AUTH_GLOBAL;
    const sendUrIPrefix = isBonus ? AUTH_BONUS_WITHDRAW : AUTH_SEND_LINK;
    const recipientType = query.get('type');
    const sendUriPostfix = recipientType ? `?type=${recipientType}` : '';
    const fullViewSearchBackUri = isBonus ? AUTH_BONUS_WITHDRAW : AUTH_SEND;
    const linkTo = isBonus ? AUTH_PROFILE_BONUS : AUTH_SEND_MAIN_LINK;

    const checkPopupAndToggleRecipientList = () => {
        if (chooseWhoToSendPopup) {
            popupRef.current?.open(<ChooseWhoToSendPopup uri={isBonus ? AUTH_BONUS_WITHDRAW : AUTH_SEND_LINK} popupRef={popupRef} />);
        } else {
            toggleRecipientList(false);
        }
    };
    const sendBrazeEventAndAttr = (country) => {
        appendToUserObjectArray(BR_RECIPIENT_COUNTRIES, {
            [BR_COUNTRY_CODE]: toUpperCase(country?.code) || '',
            [BR_SELECTED_DATE]: new Date().toISOString(),
        });
        sendBrazeEvent(BR_SEND_COUNTRY_TO_SELECTED, { [BR_COUNTRY]: country?.id?.toString() });
    };

    return (
        <>
            <CommonPage>
                {showRecipientList && (
                    <RecipientList
                        isBonus={isBonus}
                        title={recipientHeader}
                        subtitle={recipientListSubtitle}
                        linkTo={AUTH_SEND_MAIN_LINK}
                        recipientList={recipientList}
                        toggleRecipientList={checkPopupAndToggleRecipientList}
                        toggleSpinner={props.toggleSpinner}
                        recipientType={recipientType}
                    />
                )}
                {!showRecipientList && (
                    <SelectCountryScreenPureComponent
                        countries={countriesTo}
                        userCountry={userCountry}
                        title={globalSources?.length > 0 ? title : titleWithOutGlobalMethods}
                        countriesHeader={countriesHeader}
                        searchPlaceholder={searchPlaceholder}
                        fullScreenSearchPlaceholder={searchByCountryPlaceholder}
                        searchNoResult={searchNoResult}
                        gotIt={gotIt}
                        notificationText={notificationText}
                        recipientType={recipientType}
                        sendGlobalUrIPrefix={sendGlobalUrIPrefix}
                        sendUrIPrefix={sendUrIPrefix}
                        sendUriPostfix={sendUriPostfix}
                        fullViewSearchBackUri={fullViewSearchBackUri}
                        isShowSuggestedCountries={true}
                        quantityCountriesForRender={5}
                        linkTo={linkTo}
                        showCurrency={showCurrency}
                        toggleRecipientList={toggleRecipientList}
                        hideRecipientList={hideRecipientList}
                        popupRef={popupRef}
                        sendBrazeEvent={sendBrazeEventAndAttr}
                    />
                )}
            </CommonPage>
            <PopupNew ref={popupRef} />
        </>
    );
};

export default BasePage(SendSelectCountryPage);
